import { Box } from '@mui/material';
import { BarDatum, ComputedDatum } from '@nivo/bar';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth/useLoggedInUserRoles';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  AnalyticsReportDefaultProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
} from '~/data/reports/types/configTypes';
import { usePersonnelReportData } from '~/data/reports/useAnalyticsReports';
import { Roles } from '~/data/user';
import { hasOneOfRoles } from '~/services/Permissions/roles';
import { getFormattedDateString } from '~/utils/helpers/date';

import {
  ReportDatasetLayout,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '../../common';
import { ReportDataExportCustom } from '../../common/ReportDataExportCustom';

import { BarChart } from './components/BarChart/component';
import {
  DoughnutChart,
  PieComputedDatum,
} from './components/DoughnutChart/component';
import { usePersonnelReport } from './usePersonnelReport';
import { usePersonnelReportPDFExport } from './usePersonnelReportPDFExport';
import { useSxStyles } from './useStyles';

export const PersonnelReport = () => {
  const { t } = useTranslation();

  const loggedInUserRoles = useLoggedInUserRoles();
  const { orgUnits } = useAllOrgUnits();

  const sxStyles = useSxStyles();

  const [selectedSlice, setSelectedSlice] = useState<
    ComputedDatum<BarDatum> | PieComputedDatum | null
  >();

  const startDate = new Date();

  const { reportUnitOptions, disableUnitOptionsValidation } = useMemo(() => {
    const isITAdmin = hasOneOfRoles(loggedInUserRoles, [Roles.IT_ADMIN]);
    const isHRAdmin = hasOneOfRoles(loggedInUserRoles, [Roles.HR_ADMIN]);

    const canViewCompany = isITAdmin || isHRAdmin;

    const resolveUnitOptions = () => {
      if (canViewCompany) {
        return orgUnits.map((u) => u.fOrgUnitId);
      }

      return undefined;
    };

    return {
      canViewCompany,
      reportUnitOptions: resolveUnitOptions(),
      disableUnitOptionsValidation: canViewCompany,
    };
  }, [loggedInUserRoles, orgUnits]);

  const defaultReportSettings: AnalyticsReportDefaultProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: startDate,
      endDate: startDate,
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSettings,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        units: reportUnitOptions,
      },
      disableUnitOptionsValidation,
    },
  });
  const { reportSelection } = reportDatasetProps;

  const { reportTitle, exportFileNamePrefix } = useMemo(() => {
    const reportDate = reportSelection.params?.startDate;
    const reportTitle = t('laPersonelReport');

    return {
      reportTitle,
      exportFileNamePrefix: !!reportDate
        ? `${reportTitle} - ${getFormattedDateString(reportDate, 'YYYYMMDD')}`
        : reportTitle,
    };
  }, [reportSelection.params?.startDate, t]);

  const { pdfRef, onReportPDFExport } = usePersonnelReportPDFExport({
    title: reportTitle,
    startDate: reportSelection.params?.startDate,
    endDate: reportSelection.params?.endDate,
  });

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = usePersonnelReportData(reportSelection);

  const {
    numberOfEmployeesReport: {
      reportData: numberOfEmployeesReportData,
      tableData: numberOfEmployeesReportTableData,
    },
    ageReport: { ageReportData, ageReportTableData, averageAge },
    jobGradeReport: {
      jobGradeCategories,
      jobGradeReportData,
      jobGradeReportTableData,
    },
    genderReport: {
      genderDataCount,
      genderData,
      genderTableData,
      genderLegend,
    },
    employmentTypeReport: {
      employeeCount: employmentTypeEmplyeeCount,
      employmentTypeChartData,
      employmentTypeTableData,
      employmentTypeLegend,
    },
    employeeStatusReport: {
      employeeStatusCount,
      employeeStatusChartData,
      employeeStatusTableData,
      employeeStatusLegend,
    },
  } = usePersonnelReport(data);

  const handlePDFExport = () => {
    onReportPDFExport();
  };

  return (
    <ReportViewLayout
      heading={reportTitle}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
                renderCurrentParams={({ params }) => {
                  return <>{`${moment(params?.startDate).format('L')}`}</>;
                }}
                renderParamsFields={({ params, onChange }) => {
                  return (
                    <Box>
                      <ReportDateRangeSelector
                        startDate={params?.startDate}
                        endDate={params?.endDate}
                        onRangeChange={(startDate, endDate) =>
                          onChange({ ...params, startDate, endDate })
                        }
                        minDate={moment()
                          .startOf('year')
                          .subtract(9, 'years')
                          .toDate()}
                        maxDate={moment().toDate()}
                        mode={ReportDateRangeSelectorMode.SingleDateRange}
                      />
                    </Box>
                  );
                }}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <ReportDataExportCustom
              isLoading={isLoadingReportData}
              onCustomPDFExport={handlePDFExport}
            />
          ),
        },
      }}
      slotProps={{
        content: {
          sx: { height: 'auto' },
        },
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={sxStyles.chartsContainer} ref={pdfRef}>
          <BarChart
            data={numberOfEmployeesReportData}
            tableData={numberOfEmployeesReportTableData}
            label={`${t('laNumberOfEmp')}: ${
              numberOfEmployeesReportData[0]?.[t('laNumberOfEmp')] || 0
            }`}
            keys={[t('laNumberOfEmp')]}
            indexBy={'country'}
            selectedSlice={selectedSlice as ComputedDatum<BarDatum>}
            setSelectedSlice={setSelectedSlice}
            omitColumns={['age']}
            exportFileNamePrefix={exportFileNamePrefix}
          />
          <BarChart
            data={ageReportData}
            tableData={ageReportTableData}
            label={`${t('laAge')}: ${t('laAverage')} ${averageAge.toFixed(
              1,
            )} ${t('laYears')}`}
            keys={[t('laNumberOfEmp')]}
            indexBy={'category'}
            selectedSlice={selectedSlice as ComputedDatum<BarDatum>}
            setSelectedSlice={setSelectedSlice}
            omitColumns={['startDate']}
            exportFileNamePrefix={exportFileNamePrefix}
          />
          <BarChart
            data={jobGradeReportData}
            tableData={jobGradeReportTableData}
            label={t('laJobGrades')}
            keys={jobGradeCategories}
            indexBy={'jobGrade'}
            selectedSlice={selectedSlice as ComputedDatum<BarDatum>}
            setSelectedSlice={setSelectedSlice}
            omitColumns={['age']}
            exportFileNamePrefix={exportFileNamePrefix}
          />
          <DoughnutChart
            data={genderData}
            tableData={genderTableData}
            dataLegend={genderLegend}
            dataLength={genderDataCount}
            label={t('laGender')}
            selectedSlice={selectedSlice as PieComputedDatum}
            setSelectedSlice={setSelectedSlice}
            omitColumns={['age']}
            exportFileNamePrefix={exportFileNamePrefix}
          />
          <DoughnutChart
            data={employmentTypeChartData}
            tableData={employmentTypeTableData}
            dataLegend={employmentTypeLegend}
            dataLength={employmentTypeEmplyeeCount}
            label={t('laEmploymentTypes')}
            selectedSlice={selectedSlice as PieComputedDatum}
            setSelectedSlice={setSelectedSlice}
            omitColumns={['age']}
            exportFileNamePrefix={exportFileNamePrefix}
          />
          <DoughnutChart
            data={employeeStatusChartData}
            tableData={employeeStatusTableData}
            dataLegend={employeeStatusLegend}
            dataLength={employeeStatusCount}
            label={t('laEmployeeStatusesToday')}
            selectedSlice={selectedSlice as PieComputedDatum}
            setSelectedSlice={setSelectedSlice}
            omitColumns={['age']}
            exportFileNamePrefix={exportFileNamePrefix}
          />
        </Box>
      </Box>
    </ReportViewLayout>
  );
};
